import { Suspense } from "react";
import { Await } from "react-router";

import { ProductRecommendationsServer } from "~/hec/components/recs/product-recommendations-server";
import type { NoResultsRecs } from "~/routes/product-list/loader/plp-shared-utils.server";

import { useSite } from "~/contexts";
import { SITE_ELIGIBILITY } from "~/hec/config/eligibility";
import type { SerializeFrom } from "~/lib/types";

// CMS shape (`data.fields.orientationMessage`) is a single string with a
// `{}` placeholder for the query — preserved from the pre-SFCC-10663
// Contentful contract so a future Builder.io hookup keeps working. Only the
// prefix (text before `{}`) is honored; the trailing period is JSX-owned.
// When CMS data is absent, fall back to SFRA `noResultsProduct.isml` copy.
const SFRA_FALLBACK_PREFIX = "We are sorry, but no results were found for:";

interface SearchNoResultData {
  fields?: {
    orientationMessage?: string;
  };
}

interface SearchNoResultMessagesProps {
  data?: SerializeFrom<SearchNoResultData> | null;
  // Deferred no-results recommendations from the CIO `search_page_1` pod.
  // Optional: only populated when the listing resolved empty.
  recomendationData?: SerializeFrom<Promise<NoResultsRecs>>;
  query: string | null;
}

export default function SearchNoResult({
  data,
  recomendationData,
  query,
}: SearchNoResultMessagesProps) {
  const { site } = useSite();
  const eligibilityListUrl = SITE_ELIGIBILITY[site?.id ?? ""]?.listUrl;

  const beforeQuery =
    data?.fields?.orientationMessage?.split(/\{\}/)[0] ?? SFRA_FALLBACK_PREFIX;

  return (
    <>
      {query ? (
        <div className="block py-4" data-testid="no-results-message">
          <div className="mb-[21px] font-grotesque text-base font-normal leading-6 text-blue-03">
            <span>{beforeQuery}</span>{" "}
            <span className="font-bold capitalize">{query}</span>.
          </div>
          <p className="mb-[21px] font-grotesque text-base font-normal leading-6 text-blue-03">
            Need help? Try double-checking the spelling, being less specific
            {eligibilityListUrl ? (
              <>
                , or checking our{" "}
                <a href={eligibilityListUrl} className="text-blue-04">
                  <u>Eligibility List</u>
                </a>
              </>
            ) : null}
            .
          </p>
        </div>
      ) : (
        // No query → a category / filtered PLP that resolved to zero results
        // (most often a too-narrow or backwards price range). SFRA shows a
        // friendly no-results message + recommendations here rather than a
        // blank grid; mirror that so the user knows the filters — not an error
        // — emptied the page.
        <div className="block py-4" data-testid="no-results-message">
          <div className="mb-[21px] font-grotesque text-base font-normal leading-6 text-blue-03">
            We are sorry, but no results were found.
          </div>
          <p className="mb-[21px] font-grotesque text-base font-normal leading-6 text-blue-03">
            Try adjusting or removing your filters
            {eligibilityListUrl ? (
              <>
                , or checking our{" "}
                <a href={eligibilityListUrl} className="text-blue-04">
                  <u>Eligibility List</u>
                </a>
              </>
            ) : null}
            .
          </p>
        </div>
      )}
      {/* Recommendations: the SAME carousel the PDP recs use
          (ProductRecommendationsServer — Embla Carousel + RecsCarouselTile),
          fed by the CIO `search_page_1` pod. It returns null when the pod has
          no products, so an empty/failed pod simply shows nothing (no flash).
          fallback={null} avoids a skeleton flash while the deferred recs stream. */}
      {recomendationData && (
        <div className="mt-10 w-full">
          <Suspense fallback={null}>
            <Await resolve={recomendationData}>
              {recs => <ProductRecommendationsServer {...recs} />}
            </Await>
          </Suspense>
        </div>
      )}
    </>
  );
}
